import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from './Components/Header';
import MainHeader from './Components/MainHeader';
import Footer from './Components/Footer';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaCheckCircle, FaShieldAlt, FaCertificate, FaIdCard, FaArrowRight } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

// A simple CSV row parser that handles quoted strings containing commas
const parseCSVLine = (text) => {
    const result = [];
    let col = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (char === '"' && text[i + 1] === '"') {
            col += '"';
            i++;
        } else if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
            result.push(col.trim());
            col = '';
        } else {
            col += char;
        }
    }
    result.push(col.trim());
    return result;
};

const SHEET_CSV_URL = "https://docs.google.com/spreadsheets/d/1ggMI62q56A41WrdYXU3MCd4PDN79MtlEMqZd_G9zD1E/export?format=csv";

const VerificationPage = () => {
    const [searchParams] = useSearchParams();
    const [inputId, setInputId] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        AOS.init({ duration: 700, once: true });
        const id = searchParams.get('id');
        if (id) verifyCertificate(id);
    }, [searchParams]);

    const verifyCertificate = async (id) => {
        setLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await fetch(SHEET_CSV_URL);
            if (!response.ok) throw new Error("Failed to fetch database");
            
            const csvText = await response.text();
            const lines = csvText.split(/\r?\n/);
            
            let found = null;
            
            // Start from 1 to skip the header row (ID, Name, Position, DateIssued, Status)
            for (let i = 1; i < lines.length; i++) {
                const rowStr = lines[i];
                if (!rowStr.trim()) continue;
                
                const cols = parseCSVLine(rowStr);
                
                // Ensure there is an ID to match and we handle whitespace
                if (cols[0] && cols[0].trim() === id.trim()) {
                    found = {
                        id: cols[0],
                        name: cols[1] || 'N/A',
                        position: cols[2] || 'N/A',
                        dateIssued: cols[3] || 'N/A',
                        status: cols[4] || 'N/A'
                    };
                    break;
                }
            }

            if (found) {
                setResult(found);
            } else {
                setError(`Certificate ID "${id}" not found in our records.`);
            }
        } catch (err) {
            console.error("Verification error:", err);
            setError("Unable to connect to the verification database. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!inputId.trim()) {
            setError('Please enter a valid Certificate ID.');
            return;
        }
        verifyCertificate(inputId.trim());
    };

    const handleReset = () => {
        setResult(null);
        setError('');
        setInputId('');
    };

    return (
        <>
            <Header />
            <MainHeader />
            <Helmet>
                <title>Certificate Verification | Octalbees</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="vp">
                {/* Background */}
                <div className="vp-bg-grad" />

                {/* ===== HERO ===== */}
                <section className="vp-hero">
                    <p className="vp-eyebrow" data-aos="fade-up">VERIFICATION PORTAL</p>
                    <h1 className="vp-heading" data-aos="fade-up" data-aos-delay="50">
                        Verify your<br />
                        <span className="vp-heading-em">certificate</span> instantly
                    </h1>
                    <p className="vp-desc" data-aos="fade-up" data-aos-delay="100">
                        Authenticate internship &amp; offer letters officially issued by Octalbees.
                    </p>
                </section>

                {/* ===== SEARCH ===== */}
                {!result && (
                    <section className="vp-search-wrap" data-aos="fade-up" data-aos-delay="150">
                        <div className="vp-card">
                            {!loading ? (
                                <>
                                    <form onSubmit={handleSubmit} className="vp-form">
                                        <div className="vp-field">
                                            <label className="vp-label" htmlFor="certificate-id-input">Certificate ID</label>
                                            <div className="vp-input-row">
                                                <input
                                                    type="text"
                                                    placeholder="Enter your certificate ID"
                                                    value={inputId}
                                                    onChange={(e) => setInputId(e.target.value)}
                                                    className="vp-input"
                                                    id="certificate-id-input"
                                                    autoComplete="off"
                                                />
                                                <button type="submit" className="vp-btn" id="verify-btn">
                                                    Verify
                                                    <FaArrowRight className="vp-btn-arrow" />
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                    {error && (
                                        <div className="vp-error">
                                            <span className="vp-error-dot" />
                                            {error}
                                        </div>
                                    )}
                                    <p className="vp-hint">Find your certificate ID printed at the bottom-right corner of your document, or scan the QR code.</p>
                                </>
                            ) : (
                                <div className="vp-loading">
                                    <div className="vp-spinner" />
                                    <p className="vp-loading-text">Verifying certificate<span className="vp-ellipsis" /></p>
                                </div>
                            )}
                        </div>
                    </section>
                )}

                {/* ===== RESULT ===== */}
                {result && (
                    <section className="vp-result-wrap" data-aos="fade-up">
                        <div className="vp-card vp-card-result">
                            <div className="vp-result-header">
                                <div className="vp-check-wrap">
                                    <FaCheckCircle className="vp-check-icon" />
                                </div>
                                <div>
                                    <h2 className="vp-result-title">Certificate Verified</h2>
                                    <p className="vp-result-sub">This document is authentic and officially issued by Octalbees.</p>
                                </div>
                            </div>

                            <div className="vp-divider" />

                            <div className="vp-details">
                                <div className="vp-detail-row">
                                    <span className="vp-detail-label">Certificate ID</span>
                                    <span className="vp-detail-value vp-mono">{result.id}</span>
                                </div>
                                <div className="vp-detail-row">
                                    <span className="vp-detail-label">Candidate Name</span>
                                    <span className="vp-detail-value">{result.name}</span>
                                </div>
                                <div className="vp-detail-row">
                                    <span className="vp-detail-label">Role / Position</span>
                                    <span className="vp-detail-value">{result.position}</span>
                                </div>
                                <div className="vp-detail-row">
                                    <span className="vp-detail-label">Date Issued</span>
                                    <span className="vp-detail-value">{result.dateIssued}</span>
                                </div>
                                <div className="vp-detail-row">
                                    <span className="vp-detail-label">Status</span>
                                    <span className="vp-detail-value vp-status">
                                        <span className="vp-status-dot" />
                                        {result.status}
                                    </span>
                                </div>
                            </div>

                            <button className="vp-btn-reset" onClick={handleReset} id="verify-another-btn">
                                Verify another certificate
                            </button>
                        </div>
                    </section>
                )}

                {/* ===== TRUST ===== */}
                <section className="vp-trust" data-aos="fade-up" data-aos-delay="100">
                    <div className="vp-trust-row">
                        <div className="vp-trust-item">
                            <div className="vp-trust-icon"><FaShieldAlt /></div>
                            <div>
                                <strong>Secure Records</strong>
                                <span>Encrypted digital verification</span>
                            </div>
                        </div>
                        <div className="vp-trust-item">
                            <div className="vp-trust-icon"><FaCertificate /></div>
                            <div>
                                <strong>MSME Registered</strong>
                                <span>Government recognized issuer</span>
                            </div>
                        </div>
                        <div className="vp-trust-item">
                            <div className="vp-trust-icon"><FaIdCard /></div>
                            <div>
                                <strong>Unique ID &amp; QR</strong>
                                <span>Tamper-proof validation</span>
                            </div>
                        </div>
                        <div className="vp-trust-item">
                            <div className="vp-trust-icon"><FaCheckCircle /></div>
                            <div>
                                <strong>Widely Trusted</strong>
                                <span>Accepted by institutions</span>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

                .vp {
                    background: #0a0a0f;
                    color: #e4e4e7;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    position: relative;
                    overflow: hidden;
                    min-height: 82vh;
                    padding-bottom: 80px;
                }

                .vp-bg-grad {
                    position: absolute;
                    top: -200px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 900px;
                    height: 600px;
                    background: radial-gradient(ellipse at center, rgba(109, 40, 217, 0.12) 0%, transparent 65%);
                    pointer-events: none;
                    z-index: 0;
                }

                /* ========== HERO ========== */
                .vp-hero {
                    position: relative;
                    z-index: 1;
                    text-align: center;
                    padding: 60px 24px 28px;
                    max-width: 680px;
                    margin: 0 auto;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .vp-eyebrow {
                    font-size: 0.72rem;
                    font-weight: 600;
                    letter-spacing: 1.5px;
                    color: #8b5cf6;
                    margin: 0 0 16px;
                    text-transform: uppercase;
                }

                .vp-heading {
                    font-size: 3.2rem;
                    font-weight: 800;
                    letter-spacing: -1px;
                    line-height: 1.15;
                    color: #fafafa;
                    margin: 0 0 14px;
                    text-align: center;
                }

                .vp-heading-em {
                    color: #a78bfa;
                }

                .vp-desc {
                    font-size: 1.05rem;
                    color: #71717a;
                    line-height: 1.6;
                    font-weight: 300;
                    margin: 0;
                    text-align: center;
                    max-width: 460px;
                    margin-left: auto;
                    margin-right: auto;
                }

                /* ========== CARD ========== */
                .vp-search-wrap, .vp-result-wrap {
                    position: relative;
                    z-index: 1;
                    display: flex;
                    justify-content: center;
                    padding: 4px 24px 0;
                }

                .vp-card {
                    width: 100%;
                    max-width: 560px;
                    background: #111118;
                    border: none;
                    border-radius: 16px;
                    padding: 32px;
                }

                /* Form */
                .vp-form { 
                    margin: 0; 
                    border-top: none !important; 
                    border-bottom: none !important; 
                }

                .vp-field { display: flex; flex-direction: column; gap: 10px; }

                .vp-label {
                    font-size: 0.82rem;
                    font-weight: 500;
                    color: #a1a1aa;
                    letter-spacing: 0.2px;
                }

                .vp-input-row {
                    display: flex;
                    gap: 10px;
                }

                .vp-input {
                    flex: 1;
                    padding: 14px 18px;
                    border-radius: 10px;
                    background: rgba(255, 255, 255, 0.05);
                    border: 1px solid transparent;
                    color: #fafafa;
                    font-size: 0.95rem;
                    font-family: 'Inter', sans-serif;
                    outline: none;
                    transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
                    box-sizing: border-box;
                }

                .vp-input::placeholder {
                    color: #3f3f46;
                }

                .vp-input:focus {
                    border-color: #7c3aed;
                    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
                }

                .vp-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 14px 28px;
                    border-radius: 10px;
                    border: none;
                    background: #7c3aed;
                    color: #fff;
                    font-size: 0.92rem;
                    font-weight: 600;
                    font-family: 'Inter', sans-serif;
                    cursor: pointer;
                    transition: background 0.25s, transform 0.15s, box-shadow 0.25s;
                    white-space: nowrap;
                    flex-shrink: 0;
                }

                .vp-btn:hover {
                    background: #6d28d9;
                    box-shadow: 0 4px 16px rgba(109, 40, 217, 0.35);
                    transform: translateY(-1px);
                }

                .vp-btn:active { transform: translateY(0); }

                .vp-btn-arrow {
                    font-size: 0.75rem;
                    transition: transform 0.2s;
                }
                .vp-btn:hover .vp-btn-arrow {
                    transform: translateX(3px);
                }

                .vp-hint {
                    margin: 16px 0 0;
                    font-size: 0.8rem;
                    color: #52525b;
                    line-height: 1.5;
                    text-align: center;
                }

                /* Error */
                .vp-error {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-top: 16px;
                    padding: 13px 16px;
                    border-radius: 10px;
                    background: rgba(220, 38, 38, 0.06);
                    border: 1px solid rgba(220, 38, 38, 0.15);
                    color: #fca5a5;
                    font-size: 0.88rem;
                }

                .vp-error .vp-error-dot {
                    width: 6px;
                    height: 6px;
                    border-radius: 50%;
                    background: #dc2626;
                    flex-shrink: 0;
                }

                /* Loading */
                .vp-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 32px 0;
                }

                .vp-spinner {
                    width: 32px;
                    height: 32px;
                    border: 3px solid #1e1e2a;
                    border-top-color: #7c3aed;
                    border-radius: 50%;
                    animation: vpSpin 0.8s linear infinite;
                    margin-bottom: 16px;
                }

                @keyframes vpSpin { to { transform: rotate(360deg); } }

                .vp-loading-text {
                    font-size: 0.92rem;
                    color: #a1a1aa;
                    margin: 0;
                }

                .vp-ellipsis::after {
                    content: '';
                    animation: vpDots 1.2s steps(4) infinite;
                }
                @keyframes vpDots {
                    0% { content: ''; }
                    25% { content: '.'; }
                    50% { content: '..'; }
                    75% { content: '...'; }
                }

                /* ========== RESULT ========== */
                .vp-card-result {
                    max-width: 580px;
                }

                .vp-result-header {
                    display: flex;
                    align-items: flex-start;
                    gap: 18px;
                }

                .vp-check-wrap {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    background: rgba(34, 197, 94, 0.08);
                    border: 1px solid rgba(34, 197, 94, 0.15);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .vp-check-icon {
                    font-size: 1.3rem;
                    color: #22c55e;
                }

                .vp-result-title {
                    font-size: 1.3rem;
                    font-weight: 700;
                    color: #fafafa;
                    margin: 0 0 4px;
                }

                .vp-result-sub {
                    font-size: 0.88rem;
                    color: #71717a;
                    margin: 0;
                    line-height: 1.5;
                }

                .vp-divider {
                    height: 1px;
                    background: #1e1e2a;
                    margin: 24px 0;
                }

                .vp-details {
                    display: flex;
                    flex-direction: column;
                    gap: 0;
                }

                .vp-detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 14px 0;
                    border-bottom: 1px solid #17171f;
                }

                .vp-detail-row:last-child {
                    border-bottom: none;
                }

                .vp-detail-label {
                    font-size: 0.85rem;
                    color: #71717a;
                    font-weight: 400;
                }

                .vp-detail-value {
                    font-size: 0.92rem;
                    color: #e4e4e7;
                    font-weight: 600;
                    text-align: right;
                }

                .vp-mono {
                    font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', monospace;
                    letter-spacing: 0.8px;
                    color: #a78bfa;
                }

                .vp-status {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: #22c55e;
                }

                .vp-status-dot {
                    width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: #22c55e;
                    box-shadow: 0 0 6px rgba(34, 197, 94, 0.5);
                }

                .vp-btn-reset {
                    display: block;
                    width: 100%;
                    margin-top: 24px;
                    padding: 13px;
                    border-radius: 10px;
                    border: 1px solid #27272a;
                    background: transparent;
                    color: #a1a1aa;
                    font-size: 0.88rem;
                    font-weight: 500;
                    font-family: 'Inter', sans-serif;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .vp-btn-reset:hover {
                    border-color: #3f3f46;
                    color: #e4e4e7;
                    background: rgba(255,255,255,0.02);
                }

                /* ========== TRUST ========== */
                .vp-trust {
                    position: relative;
                    z-index: 1;
                    max-width: 900px;
                    margin: 0 auto;
                    padding: 40px 24px 0;
                }

                .vp-trust-row {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 1px;
                    background: #1e1e2a;
                    border-radius: 14px;
                    overflow: hidden;
                    border: 1px solid #1e1e2a;
                }

                .vp-trust-item {
                    display: flex;
                    align-items: flex-start;
                    gap: 14px;
                    padding: 24px 20px;
                    background: #111118;
                    transition: background 0.2s;
                }

                .vp-trust-item:hover {
                    background: #16161f;
                }

                .vp-trust-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 9px;
                    background: rgba(124, 58, 237, 0.08);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #7c3aed;
                    font-size: 0.9rem;
                    flex-shrink: 0;
                }

                .vp-trust-item strong {
                    display: block;
                    font-size: 0.82rem;
                    color: #d4d4d8;
                    font-weight: 600;
                    letter-spacing: 0.1px;
                    margin-bottom: 2px;
                }

                .vp-trust-item span {
                    font-size: 0.75rem;
                    color: #52525b;
                    line-height: 1.4;
                }

                /* ========== RESPONSIVE ========== */
                @media (max-width: 768px) {
                    .vp-hero { padding: 56px 20px 28px; }
                    .vp-heading { font-size: 2.5rem; letter-spacing: -1px; }
                    .vp-desc { font-size: 1rem; }
                    .vp-card { padding: 28px 24px; }

                    .vp-input-row {
                        flex-direction: column;
                    }

                    .vp-btn { justify-content: center; }

                    .vp-trust-row {
                        grid-template-columns: 1fr 1fr;
                    }

                    .vp-result-header {
                        flex-direction: column;
                        gap: 12px;
                    }

                    .vp-detail-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 4px;
                    }

                    .vp-detail-value { text-align: left; }
                }

                @media (max-width: 480px) {
                    .vp-heading { font-size: 2rem; }
                    .vp-desc { font-size: 0.92rem; }
                    .vp-card { padding: 24px 20px; border-radius: 14px; }
                    .vp-trust-row { grid-template-columns: 1fr; }
                    .vp-eyebrow { font-size: 0.65rem; letter-spacing: 2.5px; }
                }
            `}</style>
        </>
    );
};

export default VerificationPage;
